import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  facilitiesService,
  CreateFacilityInput,
  UpdateFacilityInput,
} from "../../services/admin/facilities.service";

export function useFacilitiesAdmin() {
  return useQuery({
    queryKey: ["admin", "facilities"],
    queryFn: facilitiesService.getAll,
  });
}

export function useFacility(id?: string) {
  return useQuery({
    queryKey: ["admin", "facilities", id],
    queryFn: () => facilitiesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateFacilityInput) => facilitiesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "facilities"] });
      notifications.show({
        title: "Success",
        message: "Facility created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create facility",
        color: "red",
      });
    },
  });
}

export function useUpdateFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateFacilityInput) => facilitiesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "facilities"] });
      notifications.show({
        title: "Success",
        message: "Facility updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update facility",
        color: "red",
      });
    },
  });
}

export function useToggleFacilityActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => facilitiesService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "facilities"] });
      notifications.show({
        title: "Success",
        message: "Facility status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update facility status",
        color: "red",
      });
    },
  });
}

export function useDeleteFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => facilitiesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "facilities"] });
      notifications.show({
        title: "Success",
        message: "Facility deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete facility",
        color: "red",
      });
    },
  });
}
